import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { orderService } from "./orderService";
import { useAppDispatch } from "../../store";
import { setSelectedOrder } from "./orderSlice";
import { ItemStageSidePanel, CustomerSiteDetailsSidePanel } from "../../components/common";
import type { ItemStageSidePanelRef } from "../../components/common";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useOrderRealTimeUpdates } from "../../hooks/useOrderRealTimeUpdates";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
  Collapse,
  CardContent,
  Card,
  Switch,
  FormControlLabel,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  LinearProgress,
  Grid,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  AccessTime as ClockIcon,
  CheckCircle as CheckIcon,
  LocalShipping as TruckIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Description as NotesIcon,
  Label as TagIcon,
  Timeline as TimelineIcon,
  QrCode as QrCodeIcon,
} from "@mui/icons-material";
import { RootState,  useAppSelector } from "../../store";
import { setTimelineModalMode } from "../../store/slices/timelineSlice";
import { fetchOrderById } from "./orderSlice";
import QRCodePrintView from "../../components/QRCodePrintView";
import SubItemsView from "./SubItemsView";
import PlaceOrderModal from "../site/PlaceOrderModal";
import { Order, Item, Product, ProductForm as OrderProductForm, } from "./order";
import { getProductDisplayName } from "../../utils/productUtils";
import { capitalizeWords, formatStatusText, formatDate } from "../../utils/formatters";
import DetailRow from "../../form/DetailRow";
import { motion, AnimatePresence } from "framer-motion";
import Collapsible from 'react-collapsible';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import PaletteIcon from "@mui/icons-material/Palette";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchOrders, selectSelectedOrder } from "./orderSlice";
// import { TypeRow } from "./TypeRow";
import CustumCard from "../../components/common/CustumCard";
import OrderDispatchPanel from "../../components/orders/OrderDispatchPanel";
import ItemConfigurationPanelWrapper from "./enhanced/components/ItemConfigurationPanelWrapper";
import { 
  calculateOrderStatus, 
  calculateProductStatus, 
  calculateItemStatus,
  Order as OrderWithStatus,
  Product as ProductWithStatus,
  Item as ItemWithStatus
} from "../../utils/statusCalculationUtils";
import { dispatchService } from "../../services/dispatchService";
import { v4 as uuidv4 } from "uuid";
import { enhancedApiClient, isSuccessResponse } from "../../services/enhancedApiClient";
import { ItemSection } from "./OrderForm/ItemSection";
import { ItemInfo } from "./OrderForm/ItemInfo";

import HierarchicalTimelineView from "../../components/timeline/HierarchicalTimelineView";
import ItemQRCodeGenerator from "../../components/orders/ItemQRCodeGenerator";
import CloseConfirmationDialogue from '../../components/common/__tests__/CloseConfirmationDialogue';
import { usePermission } from '../../components/common/PermissionGuard';
import { hasAnyPermission } from '../../utils/permissions';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const defaultProduct: OrderProductForm = {
  id: uuidv4(),
  type: "door",
  label: "",
  evaOrder: "",
  location: "",
  notes: "",
  netType: undefined,
  netCount: 0,
  items: [],
  frame: [],
  panel: [],
  panelWindow: [],
  net: [],
  openableType: [],
};

  // Transform product to itemTypes format for API
  const transformProductToItemTypes = (product: any) => {
    const itemTypes: Record<string, any[]> = {
      frame: [],
      panel: [],
      panelWindow: [],
      net: [],
      openableType: [],
      door: []
    };

    // Helper function to convert various boolean representations to proper boolean
    const toBoolean = (value: any, defaultValue: boolean): boolean => {
      if (value === null || value === undefined) return defaultValue;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
      return defaultValue;
    };

    // Transform items based on their type - use the updated items from the product
    product.items?.forEach((item: any) => {
      const width = (item as any)?.width || 0;
      const height = (item as any)?.height  || 0;
      
      const itemData: any = {
        width: parseInt(String(width)) || 0,
        height: parseInt(String(height)) || 0,
        quantity: item.quantity || 1,
        number: (item as any)?.number || item.itemNumber || null,
        assigned_to: item.assignedTo,
        specifications: {
          material: item.specifications?.material || 'Aluminum',
          color: item.specifications?.color || 'Silver',
          finish: item.specifications?.finish || 'Standard'
        }
      };
      
      // Include item ID if available, or set alreadyExist flag for existing items
      if (item.id) {
        itemData.id = item.id;
      } else if ((item as any)?.number || item.itemNumber) {
        // If item has a number but no ID, it's likely an existing item
        itemData.alreadyExist = true;
      }

      // Map item types to the correct category
      switch (item.type) {
        case 'Frame':
          itemTypes.frame.push(itemData);
          break;
        case 'Panel':
          itemTypes.panel.push({
            ...itemData,
            // Read actual values from item, with proper defaults
            bar_required: toBoolean((item as any)?.bar_required ?? item.specifications?.bar_required, false),
            member_required: toBoolean((item as any)?.member_required ?? item.specifications?.member_required, true),
            glass_required: toBoolean((item as any)?.glass_required ?? item.specifications?.glass_required, true),
            is_laser_cutting: toBoolean((item as any)?.is_laser_cutting, false),
          });
          break;
        case 'Panel Window':
          itemTypes.panelWindow.push(itemData);
          break;
        case 'Net Fixing':
          itemTypes.net.push(itemData);
          break;
     
        default:
          itemTypes.frame.push(itemData);
      }
    });

    return itemTypes;
  };

// Helper functions to handle both snake_case and camelCase count formats
const getFrameCount = (product: any): number => {
  return product?.frameCount ?? product?.frame_count ?? 0;
};

const getPanelCount = (product: any): number => {
  return product?.panelCount ?? product?.panel_count ?? 0;
};

const getPanelWindowCount = (product: any): number => {
  return product?.panelWindowCount ?? product?.panel_window_count ?? 0;
};

const getNetCount = (product: any): number => {
  return product?.netCount ?? product?.net_count ?? 0;
};


const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // const dispatch = useDispatch();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Permission check for order edit
  const canEditOrder = usePermission('orders', 'update');

  // Check if user has any permissions at all
  const user = useAppSelector((state: RootState) => state.auth.user);
  const userPermissions = user?.permissions;
  const userRole = user?.role;
  const userRoles = user?.roles;
  const hasAnyPermissions = hasAnyPermission(userPermissions, userRole, userRoles);

  const {
    selectedOrder: order,
    loading,
    error,
  } = useAppSelector((state: RootState) =>{
    console.log("OrderDetailsPage - Redux state:", {
      selectedOrder: state.orders.selectedOrder,
      loading: state.orders.loading,
      error: state.orders.error,
      orderId: id
    });
    return state.orders
  } );

  // Timeline settings
  const timelineSettings = useAppSelector((state: RootState) => state.timeline);

  const [qrPrintOrder, setQrPrintOrder] = useState<Order | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set<string>());
  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [openType, setOpenType] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const selectedOrder = useAppSelector(selectSelectedOrder)
  const [products, setProducts] = useState<OrderProductForm[]>([
      defaultProduct,
    ]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [activeProductType, setActiveProductType] = useState<string>("All");
  
  // Item Configuration Panel state
  const [showItemPanel, setShowItemPanel] = useState(false);
  const [selectedProductForConfig, setSelectedProductForConfig] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Side panel management
  const {
    // State
    showItemSidePanel,
    showItemStages,
    showCustomerDetailsModal,
    selectedProductForItems,
    selectedItemForStages,
    selectedCustomer,
    selectedSite,
    selectedOrder: sidePanelSelectedOrder,
    
    // Close functions
    closeAll,
    closeItemPanel,
    closeItemStagesPanel,
    closeCustomerDetails,
    
    // Back navigation functions
    backToProductsFromOrderItems,
    
    // Open functions
    openItemSidePanelFromOrder,
    openItemStagesPanel,
    openCustomerDetails,
  } = useSidePanel();
  
  const [closeDialog, setCloseDialog] = useState(false);
  
  // QR Code modal state
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [selectedItemForQR, setSelectedItemForQR] = useState<any | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderById(id) as any);
    }
  },[dispatch,id])


  // Real-time updates: Poll order data when side panel is open
  // Use refs to avoid causing re-renders - side panel handles its own updates
  const showItemSidePanelRef = React.useRef(showItemSidePanel);
  const selectedProductForItemsRef = React.useRef(selectedProductForItems);
  const showItemStagesRef = React.useRef(showItemStages);
  const selectedItemForStagesRef = React.useRef(selectedItemForStages);
  const sidePanelRef = React.useRef<ItemStageSidePanelRef>(null);
  const orderRef = React.useRef(order);
  
  // Update refs when values change - this prevents stale closures
  React.useEffect(() => {
    showItemSidePanelRef.current = showItemSidePanel;
    selectedProductForItemsRef.current = selectedProductForItems;
    showItemStagesRef.current = showItemStages;
    selectedItemForStagesRef.current = selectedItemForStages;
    orderRef.current = order;
  }, [showItemSidePanel, selectedProductForItems, showItemStages, selectedItemForStages, order]);

  // Real-time polling - updates data directly via refs without triggering full re-renders
  // Poll always enabled to keep data fresh even when panels are closed
  const { refreshOrder, isPolling } = useOrderRealTimeUpdates({
    orderId: id || null,
    enabled: true, // Always poll to keep data fresh
    intervalMs: 15000, // Poll every 15 seconds (reduced frequency to minimize disruptions)
    onUpdate: (updatedOrder) => {
      console.log('🔄 Real-time update received:', updatedOrder);
      
      // Update item side panel if open
      if (showItemSidePanelRef.current && selectedProductForItemsRef.current && updatedOrder?.products) {
        const updatedProduct = updatedOrder.products.find(
          (p: any) => p.id === selectedProductForItemsRef.current?.id
        );
        if (updatedProduct && sidePanelRef.current) {
          console.log('🔄 Updating side panel with product:', updatedProduct.id);
          // Check if data actually changed before updating
          const currentProduct = selectedProductForItemsRef.current;
          const hasChanges = JSON.stringify({
            frame: currentProduct.frame?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            panel: currentProduct.panel?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            panelWindow: currentProduct.panelWindow?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            net: currentProduct.net?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            openableType: currentProduct.openableType?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
          }) !== JSON.stringify({
            frame: updatedProduct.frame?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            panel: updatedProduct.panel?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            panelWindow: updatedProduct.panelWindow?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            net: updatedProduct.net?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
            openableType: updatedProduct.openableType?.map((i: any) => ({ id: i.id, status: i.status, stages: i.stages })),
          });
          
          if (hasChanges) {
            console.log('🔄 Product data changed, updating side panel');
            // Update side panel directly via ref - avoids Redux re-renders
            // This updates both item view and stages view if stages panel is open
            sidePanelRef.current.updateProduct(updatedProduct);
          } else {
            console.log('🔄 No changes detected in product data');
          }
        }
      }
      
      // ALWAYS update Redux to keep main table in sync
      // This ensures data refreshes even when panels are closed
      if (orderRef.current && updatedOrder) {
        console.log('🔄 Checking if order data changed...');
        // Compare order data comprehensively to detect any changes
        // Use a more efficient comparison by checking key fields
        const currentOrder = orderRef.current;
        let orderChanged = false;
        
        // Check summary stats
        if (
          currentOrder.totalProducts !== updatedOrder.totalProducts ||
          currentOrder.completedProducts !== updatedOrder.completedProducts ||
          currentOrder.status !== updatedOrder.status
        ) {
          orderChanged = true;
          console.log('🔄 Order summary stats changed');
        }
        
        // Check if any product data changed (counts, statuses, item statuses)
        if (!orderChanged && currentOrder.products && updatedOrder.products) {
          if (currentOrder.products.length !== updatedOrder.products.length) {
            orderChanged = true;
            console.log('🔄 Product count changed');
          } else {
            // Check each product for changes
            for (let i = 0; i < currentOrder.products.length; i++) {
              const currentProduct = currentOrder.products[i];
              const updatedProduct = updatedOrder.products.find(p => p.id === currentProduct.id);
              
              if (!updatedProduct) {
                orderChanged = true;
                break;
              }
              
              // Check product-level changes
              if (
                currentProduct.status !== updatedProduct.status ||
                getFrameCount(currentProduct) !== getFrameCount(updatedProduct) ||
                getPanelCount(currentProduct) !== getPanelCount(updatedProduct) ||
                getPanelWindowCount(currentProduct) !== getPanelWindowCount(updatedProduct) ||
                getNetCount(currentProduct) !== getNetCount(updatedProduct)
              ) {
                orderChanged = true;
                console.log(`🔄 Product ${currentProduct.id} changed`);
                break;
              }
              
              // Check if any item statuses changed (quick check)
              const checkItems = (items1: any[], items2: any[]) => {
                if (!items1 && !items2) return false;
                if (!items1 || !items2) return true;
                if (items1.length !== items2.length) return true;
                for (let j = 0; j < items1.length; j++) {
                  const item1 = items1[j];
                  const item2 = items2.find(i => i.id === item1.id);
                  if (!item2 || item1.status !== item2.status || item1.current_stage !== item2.current_stage) {
                    return true;
                  }
                }
                return false;
              };
              
              if (
                checkItems(currentProduct.frame, updatedProduct.frame) ||
                checkItems(currentProduct.panel, updatedProduct.panel) ||
                checkItems(currentProduct.panelWindow, updatedProduct.panelWindow) ||
                checkItems(currentProduct.net, updatedProduct.net) ||
                checkItems(currentProduct.openableType, updatedProduct.openableType)
              ) {
                orderChanged = true;
                console.log(`🔄 Items in product ${currentProduct.id} changed`);
                break;
              }
            }
          }
        }
        
        if (orderChanged) {
          console.log('🔄 Order data changed, updating Redux');
          // Update Redux silently - setSelectedOrder is a regular action, not async thunk
          // So it won't trigger loading states, just updates the data
          dispatch(setSelectedOrder(updatedOrder));
          // Update refresh trigger to refresh table only (not panels)
          // Use setTimeout to debounce and prevent rapid updates
          setTimeout(() => {
            setRefreshTrigger(prev => prev + 1);
          }, 100);
        } else {
          console.log('🔄 No changes detected in order data');
          // Even if no changes detected, update Redux to ensure sync
          // This helps catch any edge cases where comparison might miss changes
          dispatch(setSelectedOrder(updatedOrder));
        }
      } else {
        console.log('🔄 No current order or updated order to compare');
      }
    },
  });

  useEffect(() => {
  if (selectedProduct) {
    console.log("Products from OrderDetailPage======", selectedProduct);
  }
}, [selectedProduct]);

  // useEffect(() => {
  //   if (id) {
  //     dispatch(fetchOrderById(id) as any);
  //   }
  // }, [dispatch, id]);

  useEffect(()=>{
    console.log("..................selectedOrder..............", selectedOrder);
  },[])

  useEffect(()=>{
    console.log("selectedCustomer-->", selectedCustomer);
  },[selectedCustomer])
  
  // Memoize product to ensure stable reference and prevent unnecessary re-renders
  // Find product from current order to ensure we always use latest data
  // MUST be before any early returns to follow Rules of Hooks
  const currentProductForPanel = useMemo(() => {
    if (!selectedProductForItems || !order?.products) return null;
    // Find product from current order to ensure reference matches latest data
    return order.products.find(p => p.id === selectedProductForItems.id) || selectedProductForItems;
  }, [selectedProductForItems, order]);

  // Show loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <Typography variant="h6" color="error">
          Error loading order
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/orders")}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  // Show not found state
  if (!order) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <Typography variant="h6" color="text.secondary">
          Order not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The order you're looking for doesn't exist or has been removed.
        </Typography>
        <Button variant="contained" onClick={() => navigate("/orders")}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  // Show no permission state if user doesn't have any permissions
  if (!hasAnyPermissions) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
        sx={{ p: 3 }}
      >
        <Typography variant="h5" color="error" sx={{ fontWeight: 600, mb: 1 }}>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
          You don't have permission to access this page.
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please contact your administrator to request the necessary permissions.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate("/orders")}
          sx={{ mt: 2 }}
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  const handleEditOrder = (order: Order) => {
    setSelectedOrderId(order.id)
    dispatch(fetchOrderById(order.id))
    navigate(`/orders/edit/${order.id}`);
    // navigate(`/orders/new/${site?.customer?.customerId}/${site.id}`);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
  };

  // const handleOrderUpdated = (updatedOrder: any) => {
  //   console.log("Order updated successfully:", updatedOrder);
  //   // Refresh the order data
  //   if (id) {
  //     dispatch(fetchOrderById(id) as any);
  //   }
  //   setIsEditModalOpen(false);
  // };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <ClockIcon sx={{ color: "warning.main" }} />;
      case "in_progress":
        return <ClockIcon sx={{ color: "info.main" }} />;
      case "completed":
        return <CheckIcon sx={{ color: "success.main" }} />;
      case "dispatched":
        return <TruckIcon sx={{ color: "secondary.main" }} />;
      default:
        return <ClockIcon sx={{ color: "text.secondary" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "info";
      case "completed":
        return "success";
      case "dispatched":
        return "secondary";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || "Order not found"}
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/orders")}
          variant="contained"
        >
          Back to Orders
        </Button>
      </Box>
    );
  }

  const addProduct = () => {
    const newProduct: OrderProductForm = {
      id: uuidv4(),
      type: "door",
      label: "",
      evaOrder: "",
      location: "",
      notes: "",
      netType: undefined,
      netCount: 0,
      items: [],
      frame: [],
      panel: [],
      panelWindow: [],
      net: [],
      openableType: [],
    };
    setProducts([...products, newProduct]);
  };

  const handleRemoveProduct = (id: string) => {
    setProducts((prev) => prev.filter((product) => product.id !== id));
  };

  //   useEffect(() => {
  //   console.log("Order Detail===>",order)
  // },[order])

  // useEffect(()=>{
  //   console.log("order Detail====>", order);
  // },[order])

  const handleOpenItemInfo = (product: any) => {
    // Use the same ItemConfigurationPanel instead of ItemInfo
    setSelectedProductForConfig(product);
    setIsEditMode(true);
    setShowItemPanel(true);
  };

  const handleProductSelect = (product: any) => {
    console.log('Product selected for items view:', product);
    openItemSidePanelFromOrder(product);
  };

  const handleItemSelect = (item: any) => {
    console.log('Item selected for stages view:', item);
    openItemStagesPanel(item);
  };

  const handleBackToItems = () => {
    backToProductsFromOrderItems();
  };

  const handleViewQR = (item: any) => {
    console.log('Opening QR code for item:', item);
    console.log('Item QR code data:', {
      qr_code: item.qr_code,
      qr_code_image_url: item.qr_code_image_url,
      hasQrCode: !!item.qr_code,
      hasQrImageUrl: !!item.qr_code_image_url
    });
    console.log('Selected product for items:', selectedProductForItems);
    console.log('Order data:', order);
    setSelectedItemForQR(item);
    setShowQRCodeModal(true);
  };

  const handleCloseQRModal = () => {
    setShowQRCodeModal(false);
    setSelectedItemForQR(null);
  };

  // Convert order to the format expected by dispatch panel
  const convertOrderForDispatch = (order: Order): OrderWithStatus => {
    const productsWithStatus: ProductWithStatus[] = (order.products || []).map(product => {
      const itemsWithStatus: ItemWithStatus[] = (product.items || []).map(item => ({
        id: item.id,
        name: (item as any)?.number ,
        status: (item.status === 'completed' ? 2 : item.status === 'in_progress' ? 1 : 0) as 0 | 1 | 2,
        stages: [] // TODO: Implement stages from item data
      }));

      return {
        id: product.id,
        name: product.name || `Product ${product.id.slice(-4)}`,
        status: (product.status === 'completed' ? 2 : product.status === 'in_progress' ? 1 : 0) as 0 | 1 | 2 | 3,
        items: itemsWithStatus
      };
    });

    return {
      id: order.id,
      status: (order.status === 'completed' ? 2 : order.status === 'in_progress' ? 1 : 0) as 0 | 1 | 2 | 3,
      products: productsWithStatus
    };
  };

  // Handle product dispatch
  const handleDispatchProducts = async (productIds: string[]) => {
    if (!order?.id) return;
    
    setDispatchLoading(true);
    try {
      const response = await dispatchService.dispatchProducts(order.id, productIds);
      
      if (response.success && response.data) {
        alert(`Products dispatched successfully! Order status: ${response.data.orderStatus}`);
        // Refresh order data
        dispatch(fetchOrderById(order.id) as any);
      } else {
        const errorMessage = response.error?.message || 'Failed to dispatch products';
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Failed to dispatch products:', error);
      alert('Failed to dispatch products');
    } finally {
      setDispatchLoading(false);
    }
  };

  // Handle order dispatch
  const handleDispatchOrder = async () => {
    if (!order?.id) return;
    
    setDispatchLoading(true);
    try {
      const response = await dispatchService.dispatchOrder(order.id);
      
      if (response.success && response.data) {
        alert(`Order dispatched successfully! ${response.data.dispatchedProducts} products dispatched.`);
        // Refresh order data
        dispatch(fetchOrderById(order.id) as any);
      } else {
        const errorMessage = response.error?.message || 'Failed to dispatch order';
        alert(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Failed to dispatch order:', error);
      alert('Failed to dispatch order');
    } finally {
      setDispatchLoading(false);
    }
  };

  

  const TypeRow = ({ type, items }: { type: string; items: any[] }) => {
    const [open, setOpen] = React.useState(false);

    return (
      <React.Fragment>
        
        <StyledTableRow onClick={() => setOpen(!open)} style={{ cursor: "pointer" }}>
          <StyledTableCell colSpan={4} className="font-semibold">
            {open ? "▼ " : "► "} {type}
          </StyledTableCell>
        </StyledTableRow>

        <TableRow>
          <TableCell colSpan={4} style={{ padding: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ width: '100%' }}>
              <Table size="small" >
                <TableHead>
                <TableRow>
                  
                  <TableCell>ID</TableCell>
                  <TableCell>Height</TableCell>
                  <TableCell>Width</TableCell>
                  <TableCell>Stages</TableCell>
                  
                </TableRow>
              </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <StyledTableRow
                    key={item.id}
                    className="bg-white"
                    >
                      <StyledTableCell>{item.id}</StyledTableCell>
                      <StyledTableCell>{item.height || 10}</StyledTableCell>
                      <StyledTableCell>{item.width || 10}</StyledTableCell>
                      <StyledTableCell>{item.current_stage}</StyledTableCell>
                      {/* <StyledTableCell>item.current_stage, item.current_stage, item.current_stage, item.current_stage, item.current_stage, item.current_stage,item.current_stage, item.current_stage, item.current_stage, item.current_stage, item.current_stage,item.current_stage, item.current_stage, item.current_stage, item.current_stage, item.current_stage,item.current_stage, item.current_stage, item.current_stage, item.current_stage, item.current_stage,</StyledTableCell> */}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
      {/* Compact Order Header - Minimal Space */}
      <Box sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        {/* Single Row - All Essential Info */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          
        }}>
          {/* Left Side - Main Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <IconButton 
              onClick={() => navigate("/orders")}
              size="small"
              sx={{ 
                bgcolor: 'grey.100',
                '&:hover': { bgcolor: 'grey.200' }
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap'
                }}>
                  {capitalizeWords(order?.customer?.name) || "Customer Name"}
                  <Chip
                    label={`#${order?.orderNumberNew || "-"}`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                  />
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => openCustomerDetails(order?.customer, order?.site, order)}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.75rem',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'primary.dark'
                    }
                  }}
                >
                  View Details
                </Button>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" />
                  {capitalizeWords((order as any)?.site?.name) || "Site Name"}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}>
                    Order:
                  </Typography>
                  <Chip
                    label={formatStatusText(order?.status || '') || "-"}
                    size="small"
                    color={getStatusColor(order?.status) as any}
                    icon={getStatusIcon(order?.status)}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}>
                    Payment:
                  </Typography>
                  <Chip
                    label={capitalizeWords(order?.paymentStatus || "-")}
                    size="small"
                    color={order?.paymentStatus === 'paid' ? 'success' : 'warning'}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}>
                    Priority:
                  </Typography>
                  <Chip
                    label={capitalizeWords(order?.priority || "-")}
                    size="small"
                    color={getPriorityColor(order?.priority) as any}
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Center - Key Stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                {order?.totalProducts || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                Sets
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '1.1rem' }}>
              {order?.completedProducts}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                Completed
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', minWidth: 60 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', fontSize: '1.1rem' }}>
               {order?.totalProducts - order?.completedProducts}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                Pending
              </Typography>
            </Box>
          </Box>

          {/* Right Side - Actions & Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<CalendarIcon />}
              title="Due Date"
              label={order?.dueDate ? formatDate(order.dueDate) : "NA"}
              size="small"
              variant="outlined"
              color="primary"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                border: 'none', 
              }}
            />

            {/* <div
              title="Due Date" // tooltip on hover
              className="flex items-center gap-1 px-2 h-6 text-xs font-medium text-secondary  rounded-full"
              style={{ fontSize: '0.7rem', height: 24 }}
            >
              <CalendarIcon className="h-3 w-3" />
              <span>
              Due Date {order?.dueDate ? formatDate(order.dueDate) : "NA"}
              </span>
            </div> */}

            {
              order?.status !== 'completed' && canEditOrder && (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditOrder(order)}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
                >
                  Edit
                </Button>
              )
            }
            
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => setQrPrintOrder(order)}
              size="small"
              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.5 }}
            >
              Print
            </Button>
          </Box>
        </Box>
      </Box>









      {/* Products List - Main Focus */}
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 3,
        bgcolor: 'background.paper',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
      <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            📦 Set List
            <Chip
              label={`${order?.products?.length || 0} sets`}
              size="small"
              color="primary"
              sx={{ fontWeight: 600 }}
            />
        </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Click on any set to view details
          </Typography>
        </Box>

        {/* Product Type Tabs - Simplified */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
          <Tabs
            value={activeProductType}
            onChange={(e, newValue) => setActiveProductType(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 40 }}
          >
            <Tab
              value="All"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                    All
                  </Typography>
                  <Chip
                    label={order?.products?.length || 0}
                    size="small"
                    color="primary"
                    sx={{ fontSize: '0.7rem', height: 20, minWidth: 20 }}
                  />
                </Box>
              }
            />
            {(() => {
              console.log('🔄 All product types:', order?.products?.map(p => ({ name: p.name, type: p.type })));
              return order?.products?.reduce((acc: string[], product) => {
                if (product.type) {
                  const normalizedType = product.type.toUpperCase();
                  if (!acc.includes(normalizedType)) {
                    acc.push(normalizedType);
                  }
                }
                return acc;
              }, []);
            })().map((type) => {
              console.log(`🔄 Creating tab for type: ${type}`);
              const count = order?.products?.filter(p => p.type?.toUpperCase() === type).length || 0;
              return (
                <Tab
                  key={type}
                  value={type}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {type}
                      </Typography>
                      <Chip
                        label={count}
                        size="small"
                        color="secondary"
                        sx={{ fontSize: '0.7rem', height: 20, minWidth: 20 }}
                      />
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        {/* Products List - Comprehensive Summary Table */}
        <TableContainer 
          key={`products-table-${refreshTrigger}`}
          component={Paper}
          sx={{ borderRadius: 2, overflow: "hidden" }}
        >
          <Table >
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  Set Id
                </TableCell>
                {activeProductType === "All" && (
                  <TableCell sx={{ fontWeight: 600 }}>
                    Type
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>
                  Label
                </TableCell>
             
                <TableCell sx={{ fontWeight: 600 }}>
                  Location
                </TableCell>


                <TableCell sx={{ fontWeight: 600 }}>
                  Frame
                </TableCell>


                <TableCell sx={{ fontWeight: 600 }}>
                  Panel
                </TableCell>


                <TableCell sx={{ fontWeight: 600 }}>
                  Panel Window
                </TableCell>

                <TableCell sx={{ fontWeight: 600 }}>
                 Net
                </TableCell>
              
                <TableCell sx={{ fontWeight: 600 }}>
                  Stages
                </TableCell>



                <TableCell sx={{ fontWeight: 600 }}>
                  Status
                </TableCell>


                <TableCell sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order?.products
                ?.filter((product) => {
                  const shouldShow = activeProductType === "All" || 
                    product.type?.toUpperCase() === activeProductType ||
                    product.type === activeProductType;
                  console.log(`🔄 Filtering product ${product.name} (type: ${product.type}) - activeProductType: ${activeProductType}, shouldShow: ${shouldShow}`);
                  return shouldShow;
                })
                ?.map((product) => {
                  console.log('🔄 Rendering product:', product);
                      const allItems = [
                        ...(product.frame || []),
                        ...(product.panel || []),
                        ...(product.panelWindow || []),
                        ...(product.net || []),
                        ...(product.openableType || []),
                      ];
                      
                      // Calculate item statistics
                      const completedItems = allItems.filter(item => (item as any).status === 'completed').length;
                      const pendingItems = allItems.filter(item => (item as any).status === 'pending' || (item as any).status === 'in_progress').length;
                      const totalItems = allItems.length;
                      
                      // Calculate stage statistics
                      const allStages = allItems.flatMap(item => item.stages || []);
                      const completedStages = allStages.filter(stage => (stage as any).status === 'completed').length;
                      const totalStages = allStages.length;
                      
                      return (
                        <TableRow 
                          key={product.id}
                          hover
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            },                                              
                          }}
                          onClick={(e) => {
                                  e.stopPropagation();
                                  handleProductSelect(product);
                                }}
                        >
                          {/* Set Id - Display itemNumber from first item */}
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: 'text.primary'
                            }}>
                              {((product as any)?.item?.itemNumber || 
                               product?.items?.[0]?.itemNumber ||
                               product?.frame?.[0]?.itemNumber ||
                               product?.panel?.[0]?.itemNumber ||
                               product?.panelWindow?.[0]?.itemNumber ||
                               product?.net?.[0]?.itemNumber ||
                               product?.openableType?.[0]?.itemNumber ||
                               "")?.split("/")[1] || "-"}
                            </Typography>
                          </TableCell>

                          {/* Product Type - Only show in All section */}
                          {activeProductType === "All" && (
                            <TableCell>
                              <Chip
                                label={product?.type.toUpperCase() || "Unknown"}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  height: 24
                                }}
                              />
                            </TableCell>
                          )}

                          {/* Product Name */}
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 700, 
                              color: 'text.primary',
                              fontSize: '0.95rem'
                            }}>
                              {product?.label || "-"}
                            </Typography>
                          </TableCell>

                        

                          {/* Location */}
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {product?.location || "Not specified"}
                            </Typography>
                          </TableCell>


                            {/* Frame count */}
                            <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {getFrameCount(product) > 0 ? "✔" : "✖"}
                            </Typography>
                          </TableCell>


                            {/* panel count */}
                            <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {getPanelCount(product)}
                            </Typography>
                          </TableCell>


                            {/* panel window count */}
                            <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {getPanelWindowCount(product)}
                            </Typography>
                          </TableCell>


                            {/* net count */}
                            <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {getNetCount(product)}
                            </Typography>
                          </TableCell>

                          {/* Stages Information */}
                          <TableCell >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 100 }}>
                              {/* Stages Count */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 700, 
                                  color: completedStages === totalStages ? 'success.main' : completedStages > 0 ? 'info.main' : 'text.secondary',
                                  fontSize: '1.1rem'
                                }}>
                                  {completedStages}/{totalStages}
                                </Typography>
                              
                              </Box>
                              
                            
                            </Box>
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Chip
                              label={formatStatusText(product?.status || '') || "Pending"}
                              size="small"
                              color={getStatusColor(product?.status) as any}
                              icon={getStatusIcon(product?.status)}
                                    sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'row' }}>
                              {
                                product?.status !== "in_progress" && product?.status !== "completed" && canEditOrder && (
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedProductForConfig(product);
                                      setIsEditMode(true);
                                      setShowItemPanel(true);
                                    }}
                                    sx={{ 
                                      textTransform: 'none', 
                                      fontSize: '0.75rem',
                                      py: 0.5,
                                      px: 1
                                    }}
                                  >
                                    
                                  </Button>
                                )
                              }
                              
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        {/* { products.length < 10 && ( 
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              border: "2px dashed",
              borderColor: "grey.300",
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "action.hover",
                transform: "translateY(-2px)",
                boxShadow: 2,
              },
              minHeight: 180,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 2,
                background:
                  "linear-gradient(45deg, transparent 30%, rgba(0,0,0,0.02) 50%, transparent 70%)",
                opacity: 0,
                transition: "opacity 0.2s ease-in-out",
              },
              "&:hover::before": {
                opacity: 1,
              },
            }}
            onClick={addProduct}
          >
          
            <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
             
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "primary.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "primary.main",
                    fontWeight: 300,
                    lineHeight: 1,
                  }}
                >
                  +
                </Typography>
              </Box>

             
              <Typography
                variant="h6"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Click to add new Product
              </Typography>

            </Box>
          </Paper>
      </Box>
      </Paper>

      {/* Modals */}
      {isEditModalOpen && (
        <PlaceOrderModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          editMode={true}
          existingOrder={order}
        />
      )}


      {/* QR Code Print Modal */}
      {qrPrintOrder && (
        <QRCodePrintView
          order={qrPrintOrder}
          onClose={() => setQrPrintOrder(null)}
        />
      )}


      {/* Edit Order Modal */}
      {/* <PlaceOrderModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onOrderUpdated={handleOrderUpdated}
        siteData={null}
        editMode={true}
        existingOrder={order}
      /> */}
      {/*         <PlaceOrderModal
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          selectedOrderId={selectedOrderId}
        /> */}

      {/* Hierarchical Timeline View */}
      {order && (
        <HierarchicalTimelineView
          isOpen={showTimelineModal}
          onClose={() => setShowTimelineModal(false)}
          order={order}
          sidePanelMode={timelineSettings.timelineModalMode === 'sidePanel'}
        />
      )}

      {/* Order Dispatch Panel - Integrated into Product Details above */}

      {/* Item Configuration Panel */}
      {showItemPanel && selectedProductForConfig && (
        <ItemConfigurationPanelWrapper
          isOpen={showItemPanel}
          onClose={() => {
            setCloseDialog(true);
            // setShowItemPanel(false);
            // setSelectedProductForConfig(null);
            // setIsEditMode(false);
          }}
          product={selectedProductForConfig}
          onComplete={(updatedProduct) => {
            console.log('Product configuration completed:', updatedProduct);
            // Update the product in the order
            if (order && selectedProductForConfig) {
              const updatedOrder = {
                ...order,
                products: order.products?.map(p => 
                  p.id === selectedProductForConfig.id ? {
                    ...p,
                    ...updatedProduct,
                    items: (updatedProduct as any).items || []
                  } : p
                ) || []
              };
              console.log('Updated order:', updatedOrder);
              // Here you would typically dispatch an action to update the order
            }
            setShowItemPanel(false);
            setSelectedProductForConfig(null);
            setIsEditMode(false);
          }}
          isEditMode={isEditMode}
          orderId={order?.id}
          isReadOnly={isEditMode && order && (order.status === 'in_progress' || order.status === 'completed')}
          onOrderUpdated={(updatedOrder) => {
            // Child already fetched fresh order; update Redux and trigger UI refresh
            if (updatedOrder) {
              dispatch(setSelectedOrder(updatedOrder));
              setRefreshTrigger(prev => prev + 1);
            }
          }}
          onUpdate={async (updatedProduct) => {
            console.log('Product updated (local state sync):', updatedProduct);
            // Child already handled API; optionally sync optimistic local state
            if (order && updatedProduct?.id) {
              const updatedProducts = order.products?.map(p => 
                p.id === updatedProduct.id ? { ...p, ...updatedProduct } : p
              ) || [];
              dispatch(setSelectedOrder({ ...(order as any), products: updatedProducts }));
            }
            setShowItemPanel(false);
            setSelectedProductForConfig(null);
            setIsEditMode(false);
          }}
          isIncrementalMode={false}
        />
      )}

      {closeDialog && (
        <CloseConfirmationDialogue
          open={closeDialog}
          onClose={() => setCloseDialog(false)}
          onConfirm={() => {            
            setCloseDialog(false);      
            setShowItemPanel(false); 
            setSelectedProduct(null);            
          }}
          title=""
          message="Are you sure you want to close this item model?"
        />
      )}

      {/* Item & Stage Side Panel */}
      <ItemStageSidePanel
        ref={sidePanelRef}
        isOpen={showItemSidePanel}
        onClose={closeItemPanel}
        onCloseAll={closeAll}
        product={currentProductForPanel}
        onItemSelect={handleItemSelect}
        onStageSelect={(item, stage) => {
          console.log('Stage selected:', item, stage);
          // Handle stage selection if needed
        }}
        title={currentProductForPanel ? `${capitalizeWords(currentProductForPanel.type)} - Parts` : undefined}
        order={order}
      />


      {/* Customer & Site Details Side Panel */}
      <CustomerSiteDetailsSidePanel
        isOpen={showCustomerDetailsModal}
        onClose={closeCustomerDetails}
        onCloseAll={closeAll}
        customer={selectedCustomer || order?.customer || null}
        site={selectedSite || (order as any)?.site || null}
        order={sidePanelSelectedOrder || order}
        title="Customer & Site Details"
      />

      {/* Item QR Code Modal */}
      {showQRCodeModal && selectedItemForQR && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
          onClick={handleCloseQRModal}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              p: 4,
              maxWidth: 700,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
              border: '1px solid',
              borderColor: 'divider'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QrCodeIcon sx={{ color: 'primary.main', fontSize: '1.5rem' }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Item QR Code
                </Typography>
              </Box>
              <IconButton 
                onClick={handleCloseQRModal} 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { 
                    color: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                ✕
              </IconButton>
            </Box>
            
            {selectedProductForItems ? (
              <Box sx={{ 
                p: 3, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300'
              }}>
                <ItemQRCodeGenerator
                  itemId={selectedItemForQR.id}
                  orderId={order?.id || ''}
                  productId={selectedProductForItems.id || ''}
                  itemType={selectedItemForQR.type || selectedItemForQR.itemType || 'item'}
                  itemName={(selectedItemForQR as any)?.number || selectedItemForQR.id}
                  showQRCode={true}
                  existingQrCode={selectedItemForQR.qr_code}
                  existingQrImageUrl={selectedItemForQR.qr_code_image_url}
                  productName={selectedProductForItems?.name || selectedProductForItems?.type}
                  customerName={order?.customer?.name}
                  width={selectedItemForQR.width}
                  height={selectedItemForQR.height}
                  color={selectedItemForQR.color}
                />
              </Box>
            ) : (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                bgcolor: 'error.light',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'error.main'
              }}>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Error: Product information not available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Please close this modal and try again. Make sure you're viewing the item from the correct product.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseQRModal}
                  sx={{ 
                    mt: 2,
                    borderColor: 'error.main',
                    color: 'error.main',
                    '&:hover': {
                      borderColor: 'error.dark',
                      backgroundColor: 'error.light'
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrderDetailsPage;
